import { ArrowDown, ArrowUp, DotsThreeVertical } from "@phosphor-icons/react";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { DEFAULT_CURRENCY, DEFAULT_CURRENCY_SYMBOL, DEFAULT_MAX_AMOUNT } from "../../constant";
import { ISelectInput } from "../../models";
import { isNil, maskFloat, showTime, translatePhrase } from "../../utils/functions";
import Avatar from "../Avatar/Avatar";
import Button from "../Button/Button";
import EmptyPlaceholder from "../EmptyPlaceholder/EmptyPlaceholder";
import HideComponentWrapper from "../HideComponentWrapper/HideComponentWrapper";
import NumberInput from "../InputField/NumberInput";
import Link from "../Links/Link";
import List from "../Lists/List";
import Pagination from "../pagination/Pagination";
import { Popover } from "../Popover";
import Typography from "../Typography/Typography";
import CustomCellWithViewMoreCTA from "./CustomCellWithViewMoreCTA";
import styles from "./Table.module.css";
import { enumSortByValues, ITableHeader, ITableProps } from "../../models/ITableProps";

interface PopupButtonRef {
	closePopup?: () => void;
}

const Table: React.FC<ITableProps> = (props: ITableProps) => {
	const tableRef = useRef<HTMLTableElement>(null);
	const [tableWidth, setTableWidth] = useState("100%");
	const viewPopupRef = React.useRef<PopupButtonRef>(null);
	const [popupVisible, setPopupVisible] = useState(false);
	const [popupPosition, setPopupPosition] = useState({ top: 0, right: 0 });
	const [popupColumn, setPopupColumn] = useState<any>();
	const [popupRow, setPopupRow] = useState<any>();

	const popupRef = useRef<HTMLDivElement>(null);
	const [pageSize, setPageSize] = useState<number>(props.page_size);

	const transformPhrase = (phrase: string) => {
		if (props.transformPhrase && typeof props.transformPhrase === "function" && props.UiLanguage) {
			return props.transformPhrase(phrase, props.UiLanguage, {});
		} else {
			return translatePhrase(phrase) || phrase;
		}
	};

	useEffect(() => {
		const handleClickOutside = (event: any) => {
			if (popupRef.current && !popupRef.current.contains(event.target)) {
				setPopupVisible(false);
				if (tableRef.current) {
					tableRef.current.classList.remove(styles.no_scroller);
				}
			}
		};

		if (popupVisible) {
			document.addEventListener("mousedown", handleClickOutside);
			if (tableRef.current) {
				tableRef.current.classList.add(styles.no_scroller); // Disable table scroll when popup is visible
			}
		} else {
			document.removeEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			if (tableRef.current) {
				tableRef.current.classList.remove(styles.no_scroller);
			}
		};
	}, [popupVisible]);
	useEffect(() => {
		checkTableWidth();
		window.addEventListener("resize", checkTableWidth);
		return () => window.removeEventListener("resize", checkTableWidth);
	}, []);
	const checkTableWidth = () => {
		const table = tableRef.current;
		if (table) {
			const parentWidth = table.parentElement ? table.parentElement.offsetWidth : 0;
			const tableScrollWidth = table.scrollWidth;

			if (tableScrollWidth > parentWidth) {
				setTableWidth("min-content");
			} else {
				setTableWidth("100%");
			}
		}
	};
	const handleClickSort = (th: ITableHeader, value: any) => {
		props.handleDynamicSort?.(th, value);
		viewPopupRef.current?.closePopup?.();
	};
	const handlePagination = (pageNumber: number, itemsPerPage: number) => {
		if (pageNumber && !props.showLoader) {
			props.handlePagination?.(pageNumber, itemsPerPage);
		}
	};
	const onPageLimitChange = (value: number) => {
		setPageSize(value);
	};

	const showPopup = (event: any, column: any, row: any) => {
		const rect = event.target.getBoundingClientRect();
		const popupRight = window.innerWidth - rect.right;
		setPopupPosition({ top: rect.top + window.scrollY, right: popupRight });
		setPopupColumn(column);
		setPopupRow(row);
		setPopupVisible(true);
		event.stopPropagation();
	};

	return (
		<React.Fragment>
			<table
				id="table-container"
				style={{ width: tableWidth }}
				ref={tableRef}
				className={`${styles.table_container} ${props.table_class || ""} ${props.isEdit ? "edit-mode" : ""}`}
			>
				<thead>
					<tr className={`${styles.table_head} ${props.head_class || ""}`}>
						{props.headers
							?.filter((header) => !header?.hidden)
							?.map((th, thIndex) => (
								<th
									key={thIndex}
									className={`col ${styles.table_header_text} ${props.column_header_class ?? ""} ${th.th_class ?? ""}`}
								>
									<Typography type="text" size="xs" fontFamily="medium" color="tertiary">
										{transformPhrase(th.title)}
									</Typography>
									{th.total_count ? ` (${th.total_count})` : ""}
									<HideComponentWrapper hidden={!th.enable_dynamic_sort}>
										<Popover
											target={
												props.sort_value?.value === enumSortByValues.descending ? (
													<ArrowDown className="ml-2" weight="bold" />
												) : (
													<ArrowUp className="ml-2" weight="bold" />
												)
											}
											position={th.popup_positon || "bottom"}
											ref={viewPopupRef}
										>
											<List
												options={[
													{
														label: (
															<div className="d-flex align-items-center">
																<ArrowDown className="ml-0 mr-3" />
																{transformPhrase(enumSortByValues.ascending)}
															</div>
														),
														value: enumSortByValues.ascending
													},
													{
														label: (
															<div className="d-flex align-items-center">
																<ArrowUp className="ml-0 mr-3" />
																{transformPhrase(enumSortByValues.descending)}
															</div>
														),
														value: enumSortByValues.descending
													}
												]}
												setSelectedItem={(value) => handleClickSort(th, value)}
												selectedItem={props.sort_value?.value}
											/>
										</Popover>
									</HideComponentWrapper>
								</th>
							))}
					</tr>
				</thead>
				<tbody>
					{props.data?.map((item, index) => (
						<tr key={index} className={props.tr_class || ""} onClick={() => props.onRowClick?.(item)}>
							{props.headers
								.filter((header) => !header?.hidden)
								.map((th, thIndex) => {
									if (th.type === "link") {
										return (
											<td key={thIndex} className={`col ${th.td_class || ""}`}>
												<Button
													buttonType="link"
													label={th.custom_title ? th.custom_title : item[th.key] || transformPhrase("noDataAvailable")}
													onClick={() => th.handleClick?.(item)}
													size="medium"
													variant="primary"
													className={`${th.td_class || ""}`}
												/>
											</td>
										);
									} else if (th.type === "url") {
										return (
											<td key={thIndex} className={`col ${th.td_class || ""}`}>
												<Link
													aria-label="url"
													size="sm"
													fontFamily="regular"
													href={item[th.key]}
													target="_blank"
													className={`${th.td_class || "d-flex"}`}
													label={""}
												>
													{th.custom_title ? th.custom_title : item[th.key] || transformPhrase("noDataAvailable")}
												</Link>
											</td>
										);
									} else if (th.type === "button") {
										return (
											<td key={thIndex} className={`col d-flex ${th.td_class || ""}`}>
												{th.custom_title ? (
													<Button
														buttonType="button"
														label={th.custom_title}
														onClick={() => th.handleClick?.(item)}
														size="medium"
														variant="tertiary"
													/>
												) : (
													transformPhrase("noDataAvailable")
												)}
											</td>
										);
									} else if (th.type === "currency" && th.data_type === "monetary") {
										if (props.isEdit && th.editable) {
											return (
												<td key={thIndex} onClick={() => th.handleClick?.(item)} className={`col ${th.td_class || ""}`}>
													<NumberInput
														id={`${th.key}-${index}`}
														name={th.key}
														icon={"currency"}
														max={DEFAULT_MAX_AMOUNT}
														value={item[th.key]?.Amount}
														onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
															props.handleEditChange?.(th.key, e.target.value, index)
														}
														className={th.text_class || ""}
														formClass="form-class m-0"
														disableWarning={true}
														label={""}
														formSubmitted={false}
														required={false}
													/>
												</td>
											);
										} else {
											return (
												<td key={thIndex} onClick={() => th.handleClick?.(item)} className={`col ${th.td_class || ""}`}>
													<Typography type="text" size="sm" fontFamily="regular" color="tertiary">
														{isNil(item[th.key])
															? transformPhrase("noDataAvailable")
															: props.transformCurrency
																? props.transformCurrency(
																		+item[th.key]?.Amount,
																		item[th.key]?.CurrencyId || DEFAULT_CURRENCY,
																		{
																			currencyDisplay: th.currencyDisplay ?? "symbol",
																			Grouping: th.grouping ?? true,
																			minimumIntegerDigits: th.minimumIntegerDigits ?? 1
																		}
																	)
																: `${item[th.key]?.CurrencyId}${item[th.key]?.Amount}`}
													</Typography>
												</td>
											);
										}
									} else if (th.type === "currency") {
										if (props.isEdit && th.editable) {
											return (
												<td
													key={thIndex}
													onClick={() => th.handleClick?.(item)}
													className={`col d-flex ${th.td_class || ""}`}
												>
													<NumberInput
														id={`${th.key}-${index}`}
														name={th.key}
														icon={"currency"}
														max={DEFAULT_MAX_AMOUNT}
														value={item[th.key]}
														onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
															props.handleEditChange?.(th.key, e.target.value, index)
														}
														className={`row-item row-item-edit zIndex-auto`}
														formClass="form-class m-0"
														disableWarning={true}
														label={""}
														formSubmitted={false}
														required={false}
													/>
												</td>
											);
										} else {
											return (
												<td
													key={thIndex}
													onClick={() => th.handleClick?.(item)}
													className={`col d-flex ${th.td_class || ""}`}
												>
													<Typography type="text" size="sm" fontFamily="regular" color="tertiary">
														{isNil(item[th.key])
															? transformPhrase("noDataAvailable")
															: props.transformCurrency
																? props.transformCurrency(+item[th.key], DEFAULT_CURRENCY, {
																		currencyDisplay: th.currencyDisplay ?? "symbol",
																		Grouping: th.grouping ?? true,
																		minimumIntegerDigits: th.minimumIntegerDigits ?? 1
																	})
																: `${DEFAULT_CURRENCY_SYMBOL}${item[th.key]}`}
													</Typography>
												</td>
											);
										}
									} else if (th.type === "date") {
										let date = (item[th.key] && String(item[th.key])) || "";
										date = date.split("-")[0]?.length === 6 ? `20${date}` : date;

										return (
											<td key={thIndex} onClick={() => th.handleClick?.(item)} className={`col  ${th.td_class || ""}`}>
												<Typography type="text" size="sm" fontFamily="regular" color="tertiary">
													{showTime(date, th.output_date_format, th.input_date_format) ||
														transformPhrase("noDataAvailable")}
												</Typography>
											</td>
										);
									} else if (th.type === "time") {
										let time = (item[th.key] && String(item[th.key])) || "";
										let hh = time.slice(-6, -4);
										let mm = time.slice(-4, -2);
										let ss = time.slice(-2);
										let time_string = `${hh}:${mm}:${ss}`;
										let moment_time = moment(time_string, "HH:mm:ss");
										return (
											<td key={thIndex} onClick={() => th.handleClick?.(item)} className={`col  ${th.td_class || ""}`}>
												<Typography type="text" size="sm" fontFamily="regular" color="tertiary">
													{moment_time.format(th.output_date_format ?? "h:mm A") || transformPhrase("noDataAvailable")}
												</Typography>
											</td>
										);
									} else if (th.type === "jsx") {
										return (
											<td key={thIndex} onClick={() => th.handleClick?.(item)} className={`col  ${th.td_class || ""}`}>
												{th.renderer?.(item, props.UiLanguage, index)}
											</td>
										);
									} else if (th.type === "boolean") {
										return (
											<td key={thIndex} onClick={() => th.handleClick?.(item)} className={`col d-flex ${th.td_class || ""}`}>
												<Typography type="text" size="sm" fontFamily="regular" color="tertiary">
													{transformPhrase(item[th.key] ? "yes" : "no")}
												</Typography>
											</td>
										);
									} else if (th.type === "number") {
										if (props.isEdit && th.editable) {
											return (
												<td key={thIndex} onClick={() => th.handleClick?.(item)} className={`col ${th.td_class || ""}`}>
													<NumberInput
														id={`${th.key}-${index}`}
														name={th.key}
														icon={th.suffix === "%" ? "percentage" : undefined}
														max={th.suffix === "%" ? 100 : DEFAULT_MAX_AMOUNT}
														suffix={th.suffix}
														value={item[th.key]}
														onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
															props.handleEditChange?.(th.key, e.target.value, index)
														}
														className={th.text_class || ""}
														formClass="form-class m-0"
														disableWarning={true}
														label={""}
														formSubmitted={false}
														required={false}
													/>
												</td>
											);
										} else {
											return (
												<td key={thIndex} onClick={() => th.handleClick?.(item)} className={`col ${th.td_class || ""}`}>
													{isNil(item[th.key]) ? (
														transformPhrase("noDataAvailable")
													) : (
														<Typography type="text" size="sm" fontFamily="regular" color="tertiary">
															{th.decimalPlaces ? maskFloat(item[th.key], th.decimalPlaces) : item[th.key]}
															{th.suffix || ""}
														</Typography>
													)}
												</td>
											);
										}
									} else if (th.type === "sticky_button_popup") {
										return (
											<td key={thIndex} className={`col ${styles.table_sticky_end} ${th.td_class}`}>
												{th.popup_button?.(item, props.UiLanguage, index) || (
													<DotsThreeVertical
														size={24}
														weight="bold"
														onClick={(e) => showPopup(e, th, item)}
														className={"dropbtn pointer"}
													/>
												)}
											</td>
										);
									} else if (th.type === "profile") {
										return (
											<td key={thIndex} className={`col d-flex ${th.td_class || ""}`}>
												{th.user_id && item[th.user_id] ? (
													<label className="d-flex align-items-center" onClick={() => th.handleClick?.(item)}>
														<Avatar
															src_text={item[th.key] || `${item[th.first_name ?? ""]} ${item[th.last_name ?? ""]}}`}
															id={item[th.key]}
															className={`pointer ${th.pic_class || ""}`}
															image_hash={item[th.image_hash ?? ""]}
															mime_type={item[th.mime_type ?? ""] || "image/png"}
															height={"28px"}
															width={"28px"}
															parentClass="size-md"
															size="sm"
														/>
														<label onClick={() => th.handleClick?.(item)} className="ml-2 mb-0 pointer">
															{item[th.key]
																? item[th.key]
																: item[th.first_name ?? ""]
																	? `${item[th.first_name ?? ""]} ${item[th.last_name ?? ""]}`
																	: transformPhrase("noDataAvailable")}
														</label>
													</label>
												) : (
													transformPhrase("noDataAvailable")
												)}
											</td>
										);
									} else if (th.type === "action") {
										return (
											<td key={thIndex} className={`col d-flex ${th.td_class || "table_sticky_end"}`}>
												{th.popup_button?.(item, props.UiLanguage, index) || (
													<DotsThreeVertical
														onClick={(e) => showPopup(e, th, item)}
														className={`${styles.more_icon} dropbtn`}
														style={{ marginRight: "-1rem" }}
														size={"1.25rem"}
														weight="bold"
													/>
												)}
											</td>
										);
									} else {
										return (
											<CustomCellWithViewMoreCTA key={thIndex} th={th} item={item} handleShowMore={props.handleClickShowMore} />
										);
									}
								})}
						</tr>
					))}
				</tbody>
				<HideComponentWrapper hidden={props.disablePagination || (props.total_count ?? 0) <= (props.page_size ?? 0)}>
					<Pagination
						totalItems={props.total_count ?? 10}
						itemsPerPage={pageSize}
						currentPage={props.page_number ?? 1}
						onPageChange={handlePagination}
						containerClass={styles.table_pagination}
						width={tableRef.current?.scrollWidth ? `${tableRef.current.scrollWidth}px` : "100%"}
						transformPhrase={transformPhrase}
						UiLanguage={props.UiLanguage}
						onPageLimitChange={onPageLimitChange}
					/>
				</HideComponentWrapper>
				{props.renderCustomPlaceholder && !props.data?.length ? (
					props.renderCustomPlaceholder()
				) : (
					<EmptyPlaceholder
						className={styles.table_empty_placeholder}
						UiLanguage={props.UiLanguage}
						size={"xs"}
						hidden={!!props.data?.length}
					/>
				)}
				<HideComponentWrapper show={props.showLoader && !!props.loaderComponent}>
					<div className={styles.section_loader}>{props.loaderComponent}</div>
				</HideComponentWrapper>
			</table>
			{popupVisible && (
				<div
					ref={popupRef}
					className="popup-content"
					// onMouseLeave={hidePopup}
					style={{ top: `${popupPosition.top}px`, right: `${popupPosition.right}px`, position: "fixed", minWidth: "220px" }}
					onClick={(e) => e.stopPropagation()}
				>
					{popupColumn?.renderer?.(popupRow)}
				</div>
			)}
		</React.Fragment>
	);
};
Table.displayName = "Table";
export default Table;
